import type { AuthConfig, TraceHeaders } from '../types'

/** The most an auth call waits on telemetry: a slower getter sends no headers. */
export const TRACE_HEADERS_TIMEOUT_MS = 2000

const NO_HEADERS: TraceHeaders = {}

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`timed out after ${ms}ms`)),
      ms,
    )
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (err) => {
        clearTimeout(timer)
        reject(err)
      },
    )
  })

/** Throws when a name or value cannot go on the wire (fetch would reject it). */
const assertSendableHeaders = (headers: TraceHeaders): void => {
  new Headers(headers)
}

/** The extra headers for an auth call. Any failure (throw, rejection, hang,
 *  a value the Headers class rejects) is reported and yields no headers, so
 *  the auth request still goes out. */
export const resolveTraceHeaders = async (
  getTraceHeaders: AuthConfig['getTraceHeaders'],
): Promise<TraceHeaders> => {
  if (!getTraceHeaders) return NO_HEADERS
  try {
    const headers =
      (await withTimeout(
        Promise.resolve().then(getTraceHeaders),
        TRACE_HEADERS_TIMEOUT_MS,
      )) ?? NO_HEADERS
    // Validate here, not in apiFetch: a malformed name or value must take the
    // same fallback as a failing getter
    assertSendableHeaders(headers)
    return headers
  } catch (err) {
    console.error(
      '[api-client-auth] trace headers unavailable, sending auth call without them:',
      err,
    )
    return NO_HEADERS
  }
}
